import {
    type ISystemMessage,
    type ISystemMessageConfig,
    type SystemMessageContext,
    SystemMessageType,
} from "../../types/systemMessages";

import { SYSTEM_MESSAGE_TEMPLATE } from "./systemMessageTemplate";

interface ISystemMessageServiceInterface {
    createMessage: (type: SystemMessageType, context?: SystemMessageContext) => ISystemMessage;
    getMessageContent: (type: SystemMessageType, context?: SystemMessageContext) => string;
    shouldResetMessages: (type: SystemMessageType) => boolean;
    shouldSendPayload: (type: SystemMessageType) => boolean;
}

const createSystemMessageService = (): ISystemMessageServiceInterface => {
    const messageConfigs = new Map<SystemMessageType, ISystemMessageConfig>();

    const registerHandler = (type: SystemMessageType, config: ISystemMessageConfig): void => {
        messageConfigs.set(type, config);
    };

    const initializeDefaultHandlers = (): void => {
        registerHandler(SystemMessageType.ApplicationStateChange, {
            type: SystemMessageType.ApplicationStateChange,
            handler: () => SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.ApplicationStateChange].template,
            resetMessages: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.ApplicationStateChange].resetMessages,
            withPayload: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.ApplicationStateChange].withPayload,
        });

        registerHandler(SystemMessageType.NewFilterSet, {
            type: SystemMessageType.NewFilterSet,
            handler: () => SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.NewFilterSet].template,
            resetMessages: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.NewFilterSet].resetMessages,
            withPayload: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.NewFilterSet].withPayload,
        });

        registerHandler(SystemMessageType.ExistingFilterSet, {
            type: SystemMessageType.ExistingFilterSet,
            handler: () => SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.ExistingFilterSet].template,
            resetMessages: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.ExistingFilterSet].resetMessages,
            withPayload: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.ExistingFilterSet].withPayload,
        });

        registerHandler(SystemMessageType.RegenerateSuggestions, {
            type: SystemMessageType.RegenerateSuggestions,
            handler: () => SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.RegenerateSuggestions].template,
            resetMessages: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.RegenerateSuggestions].resetMessages,
            withPayload: SYSTEM_MESSAGE_TEMPLATE[SystemMessageType.RegenerateSuggestions].withPayload,
        });
    };

    const createMessage = (type: SystemMessageType, context?: SystemMessageContext): ISystemMessage => {
        const config = messageConfigs.get(type);

        if (!config) {
            throw new Error(`No handler registered for message type: ${type}`);
        }

        return {
            type,
            content: config.handler(context ?? {}),
        };
    };

    const getMessageContent = (type: SystemMessageType, context?: SystemMessageContext): string => {
        const message = createMessage(type, context);

        return message.content;
    };

    const shouldSendPayload = (type: SystemMessageType): boolean => {
        const config = messageConfigs.get(type);

        return config?.withPayload ?? true;
    };

    const shouldResetMessages = (type: SystemMessageType): boolean => {
        const config = messageConfigs.get(type);

        return config?.resetMessages ?? false;
    };

    initializeDefaultHandlers();

    return {
        createMessage,
        getMessageContent,
        shouldSendPayload,
        shouldResetMessages,
    };
};

export const systemMessageService = createSystemMessageService();
